import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthAxios from '../../axios';
import { useNavigate } from 'react-router-dom';
import SjMainImageKo from '../../assets/images/er.png';
import SjMainImageEn from '../../assets/images/er-en.png';
import SleepingJellyCouponModal, { SleepingJellyCouponModalType } from '../../components/sleepingJelly/CouponModal';
import SimpleLoading from '../../components/SimpleLoading';
import { AuthStateContext } from '../../contexts/AuthContext';
import { sjLocalStorageHandler } from '../../utils/sleepingJelly';

/**
 * 이용권 두 타입
 * - ER-4-4-4: 영문+숫자 혼합, 14자, 대시 없이 전송 (기존)
 * - ER-4-4: 숫자만 8자리, ER-NNNN-NNNN 으로 전송
 */
const LONG_SEGMENT_LENGTHS = [2, 4, 4, 4] as const;
const ER_SEGMENT_LENGTHS = [2, 4, 4] as const;
const LONG_SEGMENT_WIDTHS = [23, 71, 71, 71] as const;
const ER_SEGMENT_WIDTHS = [23, 71, 71] as const;

function sanitizeCouponChars(value: string) {
  return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
}

/** ER 뒤에 숫자만 1~8자리 — 영문이 섞이면 기존 ER-4-4-4 */
function isErNumericCode(cleaned: string) {
  return /^ER\d{1,8}$/.test(cleaned);
}

function segmentLengthsFor(cleaned: string) {
  return isErNumericCode(cleaned) ? ER_SEGMENT_LENGTHS : LONG_SEGMENT_LENGTHS;
}

function splitCouponIntoSegments(raw: string): string[] {
  const cleaned = sanitizeCouponChars(raw);
  const lengths = segmentLengthsFor(cleaned);
  const next = ['', '', '', ''];
  let cursor = 0;
  for (let i = 0; i < lengths.length; i += 1) {
    const len = lengths[i];
    next[i] = cleaned.slice(cursor, cursor + len);
    cursor += len;
  }
  return next;
}

function formatCouponCodeForApi(cleaned: string) {
  if (/^ER\d{8}$/.test(cleaned)) {
    return `ER-${cleaned.slice(2, 6)}-${cleaned.slice(6, 10)}`;
  }
  return cleaned;
}

function i18nStringList(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
}

export default function SleepingJellyCouponPage() {
  const { t, i18n } = useTranslation();
  const isEnglish = (i18n.resolvedLanguage || i18n.language || '').split('-')[0] === 'en';
  const navigate = useNavigate();
  const [segments, setSegments] = useState<string[]>(() => ['', '', '', '']);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [openModal, setModal] = useState<boolean>(false);

  const rawCode = useMemo(() => sanitizeCouponChars(segments.join('')), [segments]);
  const isErMode = isErNumericCode(rawCode);
  const segmentLengths = isErMode ? ER_SEGMENT_LENGTHS : LONG_SEGMENT_LENGTHS;
  const segmentWidths = isErMode ? ER_SEGMENT_WIDTHS : LONG_SEGMENT_WIDTHS;
  const code = useMemo(() => formatCouponCodeForApi(rawCode), [rawCode]);
  const [modalInfo, setModalInfo] = useState<SleepingJellyCouponModalType>({
    isSuccess: false,
    title: '',
    message: '',
    code: '',
  });
  const [productInfo, setProductInfo] = useState<any>(null);
  const { setLogout } = useContext(AuthStateContext);

  useEffect(() => {
    const getCouponInfo = async () => {
      setLoading(true);
      setLogout(); // 슬리핑젤리 유저는 일단 무조건 로그아웃한 상태로 시작
      try {
        const result = (await AuthAxios.get('/sleeping-jelly/coupons/product')).data;
        if (result) {
          setProductInfo(result);
        }
      } catch (err) {
        console.error('getCouponInfo', err);
      } finally {
        setLoading(false);
      }
    };
    getCouponInfo();
  }, []);

  const focusSegment = (index: number) => {
    const el = inputRefs.current[index];
    if (!el) return;
    el.focus();
    const len = el.value.length;
    el.setSelectionRange(len, len);
  };

  const handleSegmentChange = (index: number, value: string) => {
    const cleaned = sanitizeCouponChars(value);
    const maxLen = segmentLengths[index];

    setSegments((prev) => {
      const next = [...prev];
      while (next.length < 4) next.push('');
      if (cleaned.length <= maxLen) {
        next[index] = cleaned;
      } else {
        let rest = cleaned;
        for (let i = index; i < LONG_SEGMENT_LENGTHS.length && rest.length > 0; i += 1) {
          const take = LONG_SEGMENT_LENGTHS[i];
          next[i] = rest.slice(0, take);
          rest = rest.slice(take);
        }
      }
      return splitCouponIntoSegments(next.join(''));
    });

    if (cleaned.length > maxLen && index < LONG_SEGMENT_LENGTHS.length - 1) {
      requestAnimationFrame(() => focusSegment(index + 1));
    } else if (cleaned.length >= maxLen && index < segmentLengths.length - 1) {
      requestAnimationFrame(() => focusSegment(index + 1));
    }
  };

  const handleSegmentKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      void onSubmit();
      return;
    }
    if (e.key === 'Backspace' && !segments[index] && index > 0) {
      e.preventDefault();
      focusSegment(index - 1);
      return;
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      const el = e.currentTarget;
      if (el.selectionStart === 0 && el.selectionEnd === 0) {
        e.preventDefault();
        focusSegment(index - 1);
      }
      return;
    }
    if (e.key === 'ArrowRight' && index < segmentLengths.length - 1) {
      const el = e.currentTarget;
      if (el.selectionStart === el.value.length && el.selectionEnd === el.value.length) {
        e.preventDefault();
        focusSegment(index + 1);
      }
    }
  };

  const handleSegmentPaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text');
    if (!pasted) return;

    const prefix = segments.slice(0, index).join('');
    const mergedRaw = sanitizeCouponChars(prefix + pasted);
    setSegments(splitCouponIntoSegments(mergedRaw));

    const lengths = segmentLengthsFor(mergedRaw);
    const filledLen = mergedRaw.length;
    let focusAt = lengths.length - 1;
    let counted = 0;
    for (let i = 0; i < lengths.length; i += 1) {
      counted += lengths[i];
      if (filledLen < counted) {
        focusAt = i;
        break;
      }
      if (filledLen === counted) {
        focusAt = Math.min(i + 1, lengths.length - 1);
        break;
      }
    }
    requestAnimationFrame(() => focusSegment(focusAt));
  };

  const onSubmit = async () => {
    if (code) {
      let resCode = '';
      let resSuccess = false;
      try {
        setLoading(true);
        console.log(' onSubmit ~ code:', code);
        await AuthAxios.post('/sleeping-jelly/coupons/validation', { code });
        sjLocalStorageHandler('SET', code);
        navigate('/auth/login/jelly');
      } catch (err: any) {
        console.error(' onSubmit ~ err:', err);
        resCode = err?.response?.data?.message;
        const responseInformation = (await AuthAxios.get(`/sleeping-jelly/coupons/response-code/${resCode}`)).data;
        setModalInfo({ ...responseInformation.resCodeInfo, isSuccess: resSuccess });
        setModal(true);
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <>
      {loading && <SimpleLoading />}
      {openModal && <SleepingJellyCouponModal modalInfo={modalInfo} setModal={setModal} />}
      <div className="w-full h-full bg-[#13171E] flex flex-col items-center text-white pt-12 pb-4 overflow-auto">
        <div className="w-fit">
          <div className="flex justify-center items-center">
            <img
              src={isEnglish ? SjMainImageEn : SjMainImageKo}
              alt={t('sleepingJelly.heroImageAlt')}
              width={375}
              height={272}
              className="h-[272px] w-[375px] object-contain"
            />
          </div>
          <div className="px-5">
            <div className="mx-30 flex flex-col gap-5 py-8">
              <div className="flex w-full flex-col items-start">
                <p
                  style={{
                    margin: 0,
                    width: '100%',
                    fontSize: 16,
                    fontWeight: 700,
                    lineHeight: '24px',
                    letterSpacing: -0.4,
                    color: '#FFFFFF',
                  }}
                >
                  {t('sleepingJelly.heroEyebrow')}
                </p>
                <p
                  style={{
                    margin: 0,
                    width: '100%',
                    fontSize: 24,
                    fontWeight: 600,
                    lineHeight: '34px',
                    letterSpacing: -0.6,
                    color: '#FFFFFF',
                  }}
                >
                  {t('sleepingJelly.heroTitle')}
                </p>
                <p
                  style={{
                    margin: 0,
                    width: '100%',
                    fontSize: 16,
                    fontWeight: 500,
                    lineHeight: '24px',
                    letterSpacing: -0.4,
                    color: '#B0B8C1',
                  }}
                >
                  {t('sleepingJelly.heroSubtitle')}
                </p>
              </div>
              <div
                role="group"
                aria-label={t('sleepingJelly.codeGroupAria')}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 4,
                  width: '100%',
                  height: 56,
                  borderRadius: 8,
                  padding: 16,
                  border: '1px solid #333D4B',
                  background: '#191F28',
                  boxSizing: 'border-box',
                }}
              >
                {segmentLengths.map((maxLen, index) => (
                  <React.Fragment key={`coupon-seg-${index}`}>
                    <input
                      ref={(el) => {
                        inputRefs.current[index] = el;
                      }}
                      type="text"
                      inputMode="text"
                      autoCapitalize="characters"
                      autoCorrect="off"
                      spellCheck={false}
                      maxLength={maxLen}
                      value={segments[index]}
                      onChange={(e) => handleSegmentChange(index, e.target.value)}
                      onKeyDown={(e) => handleSegmentKeyDown(index, e)}
                      onPaste={(e) => handleSegmentPaste(index, e)}
                      aria-label={t('sleepingJelly.codeSegmentAria', { index: index + 1 })}
                      style={{
                        width: segmentWidths[index],
                        flexShrink: 0,
                        height: 24,
                        padding: 0,
                        margin: 0,
                        border: 'none',
                        outline: 'none',
                        background: 'transparent',
                        color: '#E5E8EB',
                        fontSize: 16,
                        fontWeight: 400,
                        lineHeight: '24px',
                        letterSpacing: -0.4,
                        textAlign: 'center',
                        caretColor: '#E5E8EB',
                      }}
                    />
                    {index < segmentLengths.length - 1 ? (
                      <span
                        aria-hidden
                        style={{
                          flexShrink: 0,
                          color: '#E5E8EB',
                          fontSize: 16,
                          fontWeight: 400,
                          lineHeight: '24px',
                          letterSpacing: -0.4,
                        }}
                      >
                        -
                      </span>
                    ) : null}
                  </React.Fragment>
                ))}
              </div>
              <button
                type="submit"
                onClick={onSubmit}
                style={{
                  width: '100%',
                  height: 60,
                  borderRadius: 50,
                  background: '#4F68FC',
                }}
              >
                {t('sleepingJelly.register')}
              </button>
              <div style={{ color: '#6B7684', fontSize: 12, fontWeight: 500 }}>
                <div>{t('sleepingJelly.howToTitle')}</div>
                <ul style={{ listStyleType: 'disc', paddingLeft: 15 }}>
                  <li>{t('sleepingJelly.howTo1')}</li>
                  <li>{t('sleepingJelly.howTo2')}</li>
                </ul>
              </div>
              <div
                className="flex flex-col gap-[10px]"
                style={{ padding: '16px 20px', borderRadius: 10, background: '#191F28' }}
              >
                <h4 className="text-[16px]">{t('sleepingJelly.multiCouponTitle')}</h4>
                <div className="text-[14px]">
                  <p>{t('sleepingJelly.multiCouponLine1')}</p>
                  <p>{t('sleepingJelly.multiCouponLine2')}</p>
                  <p>
                    {t('sleepingJelly.multiCouponLine3', {
                      limit: productInfo?.SJ_COUPON_REGISTRATION_LIMIT ?? 15,
                    })}
                  </p>
                  {t('sleepingJelly.multiCouponLine4')}
                </div>
              </div>
            </div>
            <div className="flex justify-center items-center">
              <div
                className="flex justify-center items-center text-[28px] font-bold"
                style={{
                  width: 206,
                  height: 72,
                }}
              >
                {t('sleepingJelly.noticeTitle')}
              </div>
            </div>
            <ul className="mt-[20px] text-[14px]" style={{ listStyleType: 'disc', paddingLeft: 16, fontWeight: 400 }}>
              <div>{t('sleepingJelly.noticeFull')}</div>
              <div className="mt-5">{t('sleepingJelly.noticeGeneralTitle')}</div>
              {i18nStringList(t('sleepingJelly.noticeGeneral', { returnObjects: true })).map((item) => (
                <li key={item}>{item}</li>
              ))}
              <div className="mt-5">{t('sleepingJelly.noticeEasyRestTitle')}</div>
              {i18nStringList(t('sleepingJelly.noticeEasyRest', { returnObjects: true })).map((item) => (
                <li key={item}>{item}</li>
              ))}
              <div className="mt-5">{t('sleepingJelly.noticeSoundpillTitle')}</div>
              {i18nStringList(t('sleepingJelly.noticeSoundpill', { returnObjects: true })).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </>
  );
}
